/** The quick actions every value in the external-ID drawer offers.
 *
 *  An ext-id row exists to be compared: the same value almost always appears
 *  in another tenant, in the import runs that carried it, and as a tour's or
 *  activity's own `custom_id`. So the value's action set deliberately drops
 *  the tenant scope on `/ext-ids` (that is the cross-tenant near-duplicate
 *  hunt) while keeping it on `/tours` and `/activities`, where a custom_id is
 *  only unique per operator.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 */
import type { ExtIdModel } from '@/api/extIds';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  activitiesHref,
  bookingsHref,
  cond,
  extIdsHref,
  idCond,
  importRunsHref,
  toursHref,
} from '@/utils/gridLinks';

export interface ExtIdCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: ExtIdCtx) => ctx.tenantId ?? null;

/** Console page for a mapping target, by model. Shared by the row's own
 *  target, its siblings (which may map onto a different model) and the
 *  fan-out chips. Tours land on their identifiers table (`tour#mapping`) —
 *  the mapping is what the reader came from. */
export function targetHrefFor(model: ExtIdModel, id: number): string {
  switch (model) {
    case 'itinerary':
      return `/itineraries/${id}`;
    case 'activity':
      return `/activities/${id}`;
    default:
      return `/tours/${id}#mapping`;
  }
}

export function kindActions(kind: string): ValueAction[] {
  return [act.filter(extIdsHref([cond('kind', kind)]), `Every ${kind} mapping`)];
}

export function modelActions(model: ExtIdModel): ValueAction[] {
  return [act.filter(extIdsHref([cond('model', model)]), `Mappings onto a ${model}`)];
}

/** The external id itself. */
export function valueActions(value: string, ctx: ExtIdCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(value, 'Copy value'),
    // Deliberately unscoped: the point is to find this value in OTHER tenants.
    act.filter(extIdsHref([cond('value', value)]), 'This value across all tenants'),
    act.filter(importRunsHref([cond('tour', value, 'exact')], t), 'Import runs carrying this value'),
    act.filter(toursHref([cond('custom_id', value)], t), 'Tours with this custom id'),
    act.filter(activitiesHref([cond('custom_id', value)], t), 'Activities with this custom id'),
  ];
}

/** The parked marker / "not used for matching" chip. */
export function parkedActions(): ValueAction[] {
  return [act.filter(extIdsHref([cond('matching', 'no')]), 'Every parked mapping')];
}

export function targetActions(model: ExtIdModel, id: number, name: string | null): ValueAction[] {
  const out: ValueAction[] = [act.open(targetHrefFor(model, id), `Open the ${model}`)];
  if (model === 'tour') out.push(act.open(`/tours/${id}`, 'Open the tour overview'));
  if (name) out.push(act.copy(name, 'Copy name'));
  return out;
}

/** A dangling alias: the target row is gone, so there is nothing to open. */
export function danglingActions(objectId: number): ValueAction[] {
  return [
    act.copy(String(objectId), 'Copy the missing entity id'),
    act.filter(extIdsHref([cond('dangling', 'yes')]), 'Every dangling mapping'),
  ];
}

export function tenantActions(tenant: { id: number }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(extIdsHref([], tenant.id), 'Mappings of this operator'),
  ];
}

export function idTypeActions(idType: string): ValueAction[] {
  return [act.filter(extIdsHref([cond('id_type', idType)]), `Mappings typed "${idType}"`)];
}

/** The restech (and account) a mapping is pinned to. */
export function integrationActions(
  integration: string,
  account: string | null,
  ctx: ExtIdCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.filter(extIdsHref([cond('integration', integration)], t), 'Mappings pinned to this integration'),
  ];
  if (account) {
    out.push(act.filter(extIdsHref([cond('account', account)], t), 'Mappings pinned to this account'));
  }
  if (t != null) out.push(act.open(`/tour-operators/${t}/integrations`, "Operator's integrations"));
  return out;
}

export type ExtIdFacetKey = 'import' | 'bookings' | 'api';

export function facetActions(facet: ExtIdFacetKey, state: string): ValueAction[] {
  return [
    act.filter(
      extIdsHref([cond(`used_by_${facet}`, state)]),
      `Mappings where this pipeline is "${state}"`,
    ),
  ];
}

/** A tag applied to the participant when a booking matches through the alias. */
export function tagActions(tag: { id: number; name: string }, ctx: ExtIdCtx): ValueAction[] {
  return [
    act.open(`/tags/${tag.id}`, 'Open tag'),
    act.filter(bookingsHref([idCond('tag', tag.id)], tenantOf(ctx)), 'Bookings with this tag'),
    act.copy(tag.name, 'Copy tag name'),
  ];
}

/** A fan-out target: a matching booking is ALSO added to this tour. */
export function fanoutTargetActions(target: { id: number; name: string }): ValueAction[] {
  return [
    act.open(`/tours/${target.id}#mapping`, "Open the tour's identifiers"),
    act.open(`/tours/${target.id}`, 'Open the tour'),
    act.copy(target.name, 'Copy tour name'),
  ];
}

/** The composite row id (`aeid:1234`) — copy only; `/ext-ids` has no `id`
 *  namespace to filter by. */
export function rowIdActions(id: string): ValueAction[] {
  return [act.copy(id, 'Copy row id')];
}
